import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import jsQR from "jsqr";
import { Toast } from "../../components/Toast";
import { buildShareLink, parseSharedText } from "../../logic/qrShareLink";

type TabKey = "generate" | "scan";

export function QRCodeGenerator() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>("generate");
  const [inputText, setInputText] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState("");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanResultRef = useRef("");

  const generateQrCode = async (text?: string) => {
    const value = (text ?? inputText).trim();
    if (!value) {
      setQrDataUrl(null);
      return;
    }

    try {
      const dataUrl = await QRCode.toDataURL(value, {
        width: 320,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      });
      setQrDataUrl(dataUrl);
    } catch {
      setToastMessage(t("qrGenerator.generateFailed"));
    }
  };

  const copyShareLink = async () => {
    const link = buildShareLink(window.location.href, inputText);
    if (!link) {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setToastMessage(t("qrGenerator.linkCopied"));
    } catch {
      setToastMessage(t("qrGenerator.copyFailed"));
    }
  };

  const downloadQrCode = () => {
    if (!qrDataUrl) {
      return;
    }
    const link = document.createElement("a");
    link.href = qrDataUrl;
    link.download = "qr-code.png";
    link.click();
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToastMessage(t("qrGenerator.copied"));
    } catch {
      setToastMessage(t("qrGenerator.copyFailed"));
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const loadCameras = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices.filter((device) => device.kind === "videoinput");
    setAvailableCameras(cameras);
    if (!activeDeviceId && cameras.length > 0) {
      setActiveDeviceId(cameras[0].deviceId);
    }
  };

  const startCamera = async (deviceId?: string) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("qrGenerator.cameraUnsupported"));
      return;
    }

    setCameraError(null);
    stopStream();

    try {
      const constraints: MediaStreamConstraints = {
        video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: "environment" },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
      await loadCameras();
    } catch {
      setCameraError(t("qrGenerator.cameraError"));
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    stopStream();
    setCameraActive(false);
  };

  const switchCamera = async () => {
    if (availableCameras.length < 2) {
      return;
    }
    const currentIndex = availableCameras.findIndex(
      (camera) => camera.deviceId === activeDeviceId
    );
    const nextIndex = (currentIndex + 1) % availableCameras.length;
    const nextDevice = availableCameras[nextIndex];
    setActiveDeviceId(nextDevice.deviceId);
    await startCamera(nextDevice.deviceId);
  };

  useEffect(() => {
    if (!cameraActive) {
      return;
    }

    let rafId = 0;
    const scanFrame = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState >= 2) {
        const width = video.videoWidth;
        const height = video.videoHeight;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0, width, height);
          const imageData = ctx.getImageData(0, 0, width, height);
          const code = jsQR(imageData.data, width, height);
          if (code?.data && code.data !== scanResultRef.current) {
            scanResultRef.current = code.data;
            setScanResult(code.data);
          }
        }
      }
      rafId = window.requestAnimationFrame(scanFrame);
    };

    rafId = window.requestAnimationFrame(scanFrame);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [cameraActive]);

  useEffect(() => {
    loadCameras();
    return () => {
      stopStream();
    };
  }, []);

  useEffect(() => {
    const shared = parseSharedText(window.location.search);
    if (shared) {
      setActiveTab("generate");
      setInputText(shared);
      void generateQrCode(shared);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "generate") {
      stopCamera();
    }
  }, [activeTab]);

  return (
    <div className="qr-generator">
      <div className="qr-tabs">
        <button
          type="button"
          className={`qr-tab ${activeTab === "generate" ? "active" : ""}`}
          onClick={() => setActiveTab("generate")}
        >
          {t("qrGenerator.tabs.generate")}
        </button>
        <button
          type="button"
          className={`qr-tab ${activeTab === "scan" ? "active" : ""}`}
          onClick={() => setActiveTab("scan")}
        >
          {t("qrGenerator.tabs.scan")}
        </button>
      </div>

      {activeTab === "generate" ? (
        <div className="qr-panel">
          <label className="qr-label" htmlFor="qr-input">
            {t("qrGenerator.inputLabel")}
          </label>
          <input
            id="qr-input"
            className="qr-input"
            type="text"
            placeholder={t("qrGenerator.inputPlaceholder")}
            value={inputText}
            onChange={(event) => setInputText(event.target.value)}
          />
          <button
            type="button"
            className="qr-primary-btn"
            onClick={() => generateQrCode()}
          >
            {t("qrGenerator.generate")}
          </button>

          <div className="qr-result-card">
            {qrDataUrl ? (
              <>
                <img
                  src={qrDataUrl}
                  alt={t("qrGenerator.qrAlt")}
                  className="qr-image"
                />
                <div className="qr-actions">
                  <button type="button" className="qr-secondary-btn" onClick={downloadQrCode}>
                    {t("qrGenerator.download")}
                  </button>
                  <button
                    type="button"
                    className="qr-secondary-btn"
                    onClick={() => copyToClipboard(inputText)}
                    disabled={!inputText.trim()}
                  >
                    {t("qrGenerator.copyText")}
                  </button>
                  <button
                    type="button"
                    className="qr-secondary-btn"
                    onClick={copyShareLink}
                    disabled={!inputText.trim()}
                  >
                    {t("qrGenerator.copyLink")}
                  </button>
                </div>
              </>
            ) : (
              <p className="qr-placeholder">{t("qrGenerator.qrPlaceholder")}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="qr-panel">
          <div className="qr-scan-actions">
            <button
              type="button"
              className="qr-primary-btn"
              onClick={() => startCamera(activeDeviceId ?? undefined)}
              disabled={cameraActive}
            >
              {t("qrGenerator.startCamera")}
            </button>
            <button
              type="button"
              className="qr-secondary-btn"
              onClick={stopCamera}
              disabled={!cameraActive}
            >
              {t("qrGenerator.stopCamera")}
            </button>
            <button
              type="button"
              className="qr-secondary-btn"
              onClick={switchCamera}
              disabled={availableCameras.length < 2}
            >
              {t("qrGenerator.switchCamera")}
            </button>
          </div>

          {cameraError && <p className="qr-error">{cameraError}</p>}

          <div className="qr-scan-area">
            <video ref={videoRef} className="qr-video" playsInline muted />
            <canvas ref={canvasRef} className="qr-canvas" aria-hidden="true" />
            {!cameraActive && (
              <p className="qr-placeholder">{t("qrGenerator.scanPlaceholder")}</p>
            )}
          </div>

          <div className="qr-scan-result">
            <span className="qr-result-label">{t("qrGenerator.scanResultLabel")}</span>
            <div className="qr-result-value">
              {scanResult ? scanResult : t("qrGenerator.scanNoResult")}
            </div>
            <button
              type="button"
              className="qr-secondary-btn"
              onClick={() => copyToClipboard(scanResult)}
              disabled={!scanResult}
            >
              {t("qrGenerator.copyResult")}
            </button>
          </div>
        </div>
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
